import * as THREE from 'three';
import type { BridgeRoute } from '../../embed/MapBridge';
import type { IMapSurface } from './MapSurface';

export interface MapTubeRouteRendererConfig {
    surface: IMapSurface;
    parent: THREE.Object3D;
    radiusMeters?: number;
    liftMeters?: number;
    radialSegments?: number;
    minSamples?: number;
    maxSamples?: number;
    samplesPerSourcePoint?: number;
}

/** Terrain-draped route strategy used by the curated GLB map. */
export class MapTubeRouteRenderer {
    private readonly config: Required<Omit<MapTubeRouteRendererConfig, 'surface' | 'parent'>>
        & Pick<MapTubeRouteRendererConfig, 'surface' | 'parent'>;
    private readonly group = new THREE.Group();
    private readonly routeMeshes = new Map<string, THREE.Mesh[]>();
    private routes: BridgeRoute[] = [];
    private selectedRouteId: string | null = null;

    constructor(config: MapTubeRouteRendererConfig) {
        this.config = {
            radiusMeters: 0.9,
            liftMeters: 2,
            radialSegments: 6,
            minSamples: 64,
            maxSamples: 600,
            samplesPerSourcePoint: 12,
            ...config,
        };
        this.group.name = 'BridgeRoutes';
        config.parent.add(this.group);
    }

    setRoutes(routes: BridgeRoute[]): void {
        this.routes = routes.map(route => ({
            ...route,
            points: route.points.map(point => ({ ...point })),
        }));
        this.rebuild();
    }

    selectRoute(routeId: number | string | null): void {
        this.selectedRouteId = routeId === null ? null : String(routeId);
        for (const [id, meshes] of this.routeMeshes) {
            const dimmed = this.selectedRouteId !== null && id !== this.selectedRouteId;
            for (const mesh of meshes) {
                (mesh.material as THREE.MeshBasicMaterial).opacity = dimmed ? 0.3 : 1;
            }
        }
    }

    refreshSurface(): void {
        this.rebuild();
    }

    dispose(): void {
        this.clearMeshes();
        this.group.removeFromParent();
    }

    private rebuild(): void {
        this.clearMeshes();
        const units = this.config.surface.worldUnitsPerMeter;
        const radius = this.config.radiusMeters * units;
        const lift = this.config.liftMeters * units;

        for (const route of this.routes) {
            const raw = route.points
                .map(point => this.config.surface.geoToWorld(point.lat, point.lng))
                .filter((point): point is THREE.Vector3 => point !== null)
                .map(point => new THREE.Vector3(point.x, 0, point.z));
            if (raw.length < 2) continue;

            const sampleCount = Math.min(
                this.config.maxSamples,
                Math.max(this.config.minSamples, raw.length * this.config.samplesPerSourcePoint),
            );
            const flatCurve = new THREE.CatmullRomCurve3(raw);
            const segments: THREE.Vector3[][] = [];
            let current: THREE.Vector3[] = [];
            for (const point of flatCurve.getPoints(sampleCount)) {
                const groundY = this.config.surface.getWorldY(point.x, point.z);
                if (groundY === null) {
                    if (current.length >= 2) segments.push(current);
                    current = [];
                } else {
                    current.push(new THREE.Vector3(point.x, groundY + lift, point.z));
                }
            }
            if (current.length >= 2) segments.push(current);

            const material = new THREE.MeshBasicMaterial({
                color: new THREE.Color(route.color || '#ff4444'),
                transparent: true,
                opacity: 1,
            });
            const meshes: THREE.Mesh[] = [];
            for (const segment of segments) {
                const curve = new THREE.CatmullRomCurve3(segment);
                const geometry = new THREE.TubeGeometry(
                    curve,
                    segment.length,
                    radius,
                    this.config.radialSegments,
                    false,
                );
                const mesh = new THREE.Mesh(geometry, material);
                meshes.push(mesh);
                this.group.add(mesh);
            }
            if (meshes.length === 0) material.dispose();
            this.routeMeshes.set(String(route.id), meshes);
        }
        this.selectRoute(this.selectedRouteId);
    }

    private clearMeshes(): void {
        const disposedMaterials = new Set<THREE.Material>();
        for (const meshes of this.routeMeshes.values()) {
            for (const mesh of meshes) {
                mesh.removeFromParent();
                mesh.geometry.dispose();
                const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                for (const material of materials) {
                    if (!disposedMaterials.has(material)) {
                        material.dispose();
                        disposedMaterials.add(material);
                    }
                }
            }
        }
        this.routeMeshes.clear();
        this.group.clear();
    }
}
